import type { ConnectionState } from '../api/events';
import type { Room } from '../editor/rooms';

export interface ConnectionBannerProps {
  /** server-sent events: comments, decisions and publishes */
  events: ConnectionState;
  /** the Yjs room behind the open story, when one is open */
  room?: Room['status'];
  onRetry?(): void;
}

/**
 * What is still safe to do when the connection drops. Edits keep working because Yjs buffers them
 * locally and replays on reconnect; only other people's activity stops arriving. Say exactly that
 * rather than a bare "offline".
 */
export function connectionMessage(events: ConnectionState, room?: Room['status']): string | null {
  const roomDown = room === 'disconnected';
  if (events === 'disconnected' && roomDown) return 'You are offline. Your edits are kept in this tab and will sync when the connection returns.';
  if (roomDown) return 'The shared document is offline. Keep typing: your edits are kept in this tab and will sync when it reconnects.';
  if (events === 'disconnected') return 'Live updates are disconnected. Editing still works; new comments and decisions will appear after a refresh.';
  return null;
}

export function ConnectionBanner({ events, room, onRetry }: ConnectionBannerProps) {
  const message = connectionMessage(events, room);
  if (!message) return null;
  return (
    <div className="banner warn" role="status">
      <span className="grow">{message}</span>
      {onRetry && <button className="btn sm ghost" style={{ color: 'var(--paper)' }} onClick={onRetry}>Reload</button>}
    </div>
  );
}
